/**
 * MessagesPage
 *
 * Full messaging interface with split-view layout:
 * - Left sidebar: ConversationList
 * - Right panel: MessageThread
 *
 * Features:
 * - Responsive design (full-width conversation on mobile)
 * - Conversation actions (archive, mute, delete)
 * - WebSocket integration for real-time updates
 * - URL-based conversation selection
 * - Phase 5: Message search, advanced actions, rich content
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AlertTriangle,
  MessageSquare,
  Menu,
  RefreshCw,
  X,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConversationList } from '@/components/messages/ConversationList';
import { MessageThread } from '@/components/messages/MessageThread';
import { NewConversationModal } from '@/components/messages/NewConversationModal';
import { MessageSearch } from '@/components/messages/MessageSearch';
import { ConvertToGroupModal } from '@/components/messages/ConvertToGroupModal';
import {
  useConversations,
  useActiveConversation,
  useActiveConversationId,
  useMessages,
  usePendingMessages,
  useTypingIndicator,
  useMessagingLoading,
  useHasMoreMessages,
  useMessagingActions,
  useComposingState,
  useMessagingWebSocketStatus,
  isFailedPendingMessageError,
  isConversationUnavailableError,
  setupMessagingWebSocketListeners,
  setMessagingConversationNavigator,
} from '@/stores/messaging';
import { useAuth } from '@/stores/auth/auth-store';
import { getErrorMessageOrFallback } from '@/utils/errors';
import { logger } from '@/utils/logger';
import type { Conversation } from '@/types/messaging';

const logMessagingPageAsyncError = (
  operation: string,
  error: unknown,
): void => {
  logger.warn('Messages page action failed', {
    operation,
    error: getErrorMessageOrFallback(error, 'Unexpected messaging page error'),
  });
};

export const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Store hooks
  const conversations = useConversations();
  const activeConversation = useActiveConversation();
  const activeConversationId = useActiveConversationId();
  const messages = useMessages();
  const pendingMessages = usePendingMessages();
  const {
    isLoadingConversations,
    isLoadingMessages,
    isSendingMessage,
    isLoadingMore,
  } = useMessagingLoading();
  const { isSettingUpWebSocketListeners, websocketListenerError } =
    useMessagingWebSocketStatus();
  const hasMoreMessages = useHasMoreMessages();
  const {
    fetchConversations,
    setActiveConversation,
    loadMoreMessages,
    sendMessage,
    retryPendingMessage,
    removePendingMessage,
    editMessage,
    deleteMessage,
    archiveConversation,
    unarchiveConversation,
    muteConversation,
    unmuteConversation,
    emitTypingStart,
    emitTypingStop,
    // Composing actions
    startComposing,
    cancelComposing,
    addComposingRecipient,
    removeComposingRecipient,
    sendComposingMessage,
  } = useMessagingActions();

  // Composing state
  const { isComposing, composingRecipients } = useComposingState();

  // Get typing users for active conversation
  const typingUserIds = useTypingIndicator(activeConversationId || '');

  // UI state
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showConvertToGroupModal, setShowConvertToGroupModal] = useState(false);
  const pendingUrlConversationRef = useRef<string | null>(null);
  const lastSelectedUrlConversationRef = useRef<string | null>(null);

  const clearConversationSearchParam = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('conversation');
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    setMessagingConversationNavigator((path) => {
      navigate(path);
    });

    return () => {
      setMessagingConversationNavigator(null);
    };
  }, [navigate]);

  // Fetch conversations on mount (force refresh to get latest including archived)
  useEffect(() => {
    void fetchConversations(true).catch((error) => {
      logMessagingPageAsyncError('fetch_conversations', error);
    });
  }, [fetchConversations]);

  useEffect(() => {
    setupMessagingWebSocketListeners();
  }, []);

  // Handle URL-based conversation selection and compose mode
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    const composeParam = searchParams.get('compose');

    // Handle compose mode
    if (composeParam === 'true' && !isComposing) {
      startComposing();
      setShowMobileSidebar(false);
      return;
    }

    if (
      conversationId &&
      activeConversationId === null &&
      lastSelectedUrlConversationRef.current === conversationId &&
      pendingUrlConversationRef.current !== conversationId
    ) {
      return;
    }

    // Handle conversation selection
    if (conversationId && conversationId !== activeConversationId) {
      // Exit compose mode if we're selecting a conversation
      if (isComposing) {
        cancelComposing();
      }
      pendingUrlConversationRef.current = conversationId;
      void setActiveConversation(conversationId)
        .then(() => {
          lastSelectedUrlConversationRef.current = conversationId;
        })
        .catch((error) => {
          logMessagingPageAsyncError('set_active_conversation_from_url', error);
          if (
            pendingUrlConversationRef.current === conversationId &&
            isConversationUnavailableError(error)
          ) {
            clearConversationSearchParam();
          }
        })
        .finally(() => {
          if (pendingUrlConversationRef.current === conversationId) {
            pendingUrlConversationRef.current = null;
          }
        });
      setShowMobileSidebar(false);
    }
  }, [
    searchParams,
    activeConversationId,
    setActiveConversation,
    isComposing,
    startComposing,
    cancelComposing,
    clearConversationSearchParam,
  ]);

  useEffect(() => {
    const conversationId = searchParams.get('conversation');

    if (conversationId && activeConversationId === conversationId) {
      lastSelectedUrlConversationRef.current = conversationId;
      return;
    }

    if (
      conversationId &&
      activeConversationId === null &&
      pendingUrlConversationRef.current !== conversationId &&
      lastSelectedUrlConversationRef.current === conversationId
    ) {
      clearConversationSearchParam();
      lastSelectedUrlConversationRef.current = null;
    }
  }, [searchParams, activeConversationId, clearConversationSearchParam]);

  /**
   * Handle conversation selection
   */
  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      setSearchParams({ conversation: conversationId });
      void setActiveConversation(conversationId).catch((error) => {
        logMessagingPageAsyncError('select_conversation', error);
      });
      setShowMobileSidebar(false);
    },
    [setSearchParams, setActiveConversation],
  );

  /**
   * Handle send message
   */
  const handleSendMessage = useCallback(
    async (content: string, replyToId?: string) => {
      try {
        await sendMessage(content, replyToId);
      } catch (error) {
        if (isFailedPendingMessageError(error)) {
          return;
        }

        throw error;
      }
    },
    [sendMessage],
  );

  /**
   * Handle edit message
   */
  const handleEditMessage = useCallback(
    async (messageId: string, content: string) => {
      await editMessage(messageId, content);
    },
    [editMessage],
  );

  /**
   * Handle delete message
   */
  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      await deleteMessage(messageId);
    },
    [deleteMessage],
  );

  /**
   * Handle archive conversation
   */
  const handleArchive = useCallback(async () => {
    if (activeConversationId) {
      await archiveConversation(activeConversationId);
    }
  }, [activeConversationId, archiveConversation]);

  /**
   * Handle unarchive conversation
   */
  const handleUnarchive = useCallback(async () => {
    if (activeConversationId) {
      await unarchiveConversation(activeConversationId);
    }
  }, [activeConversationId, unarchiveConversation]);

  /**
   * Handle mute conversation with duration
   */
  const handleMute = useCallback(
    async (mutedUntil: Date) => {
      if (activeConversationId) {
        await muteConversation(activeConversationId, mutedUntil);
      }
    },
    [activeConversationId, muteConversation],
  );

  /**
   * Handle unmute conversation
   */
  const handleUnmute = useCallback(async () => {
    if (activeConversationId) {
      await unmuteConversation(activeConversationId);
    }
  }, [activeConversationId, unmuteConversation]);

  /**
   * Handle typing indicators
   */
  const handleTypingStart = useCallback(() => {
    if (activeConversationId) {
      emitTypingStart(activeConversationId);
    }
  }, [activeConversationId, emitTypingStart]);

  const handleTypingStop = useCallback(() => {
    if (activeConversationId) {
      emitTypingStop(activeConversationId);
    }
  }, [activeConversationId, emitTypingStop]);

  /**
   * Handle navigation to a specific message from search results
   */
  const handleNavigateToMessage = useCallback(
    (conversationId: string, _messageId: string) => {
      // First select the conversation
      setSearchParams({ conversation: conversationId });
      void setActiveConversation(conversationId).catch((error) => {
        logMessagingPageAsyncError('navigate_to_message', error);
      });
      // TODO: Scroll to specific message (would require MessageThread enhancement)
      // For now, just navigating to the conversation
    },
    [setSearchParams, setActiveConversation],
  );

  /**
   * Handle canceling compose mode
   */
  const handleCancelComposing = useCallback(() => {
    cancelComposing();
    // Clear compose param from URL
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('compose');
      return newParams;
    });
  }, [cancelComposing, setSearchParams]);

  /**
   * Handle opening an existing conversation from compose mode
   */
  const handleOpenExistingConversation = useCallback(
    (conversation: Conversation) => {
      cancelComposing();
      setSearchParams({ conversation: conversation.id });
      void setActiveConversation(conversation.id).catch((error) => {
        logMessagingPageAsyncError('open_existing_conversation', error);
      });
    },
    [cancelComposing, setSearchParams, setActiveConversation],
  );

  /**
   * Handle starting compose mode (for "New" button)
   */
  const handleStartComposing = useCallback(() => {
    setSearchParams({ compose: 'true' });
  }, [setSearchParams]);

  /**
   * Handle sending a message in compose mode
   */
  const handleComposingSend = useCallback(
    async (content: string) => {
      const conversation = await sendComposingMessage(content);
      if (conversation) {
        setSearchParams({ conversation: conversation.id });
        return;
      }

      throw new Error('Failed to send message');
    },
    [sendComposingMessage, setSearchParams],
  );

  /**
   * Handle convert to group (opens modal)
   */
  const handleConvertToGroup = useCallback(() => {
    setShowConvertToGroupModal(true);
  }, []);

  const handleRetryWebSocketListeners = useCallback(() => {
    setupMessagingWebSocketListeners();
  }, []);

  return (
    <>
      <Helmet>
        <title>Messages - TerraMatrix</title>
      </Helmet>

      <div className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between p-4 border-b bg-background">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Messages</h1>
          </div>
          <div className="flex items-center gap-1">
            {/* Search button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSearchModal(true)}
              className="h-9 w-9 p-0"
            >
              <Search className="h-5 w-5" />
              <span className="sr-only">Search messages</span>
            </Button>
            {/* Menu toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMobileSidebar(!showMobileSidebar)}
              className="md:hidden"
            >
              {showMobileSidebar ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
              <span className="sr-only">
                {showMobileSidebar ? 'Close sidebar' : 'Open sidebar'}
              </span>
            </Button>
          </div>
        </div>

        {websocketListenerError && (
          <div
            role="alert"
            className="flex flex-col gap-3 border-b border-[hsl(var(--feedback-warning)/0.3)] bg-[hsl(var(--feedback-warning)/0.1)] px-4 py-3 text-foreground md:flex-row md:items-center md:justify-between"
          >
            <div className="flex min-w-0 items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--feedback-warning))]" />
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  Realtime message updates are paused
                </p>
                <p className="break-words text-xs text-muted-foreground">
                  {websocketListenerError}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRetryWebSocketListeners}
              disabled={isSettingUpWebSocketListeners}
              className="w-full border-[hsl(var(--feedback-warning)/0.3)] hover:bg-[hsl(var(--feedback-warning)/0.1)] md:w-auto"
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${isSettingUpWebSocketListeners ? 'animate-spin' : ''}`}
              />
              Retry
            </Button>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar - conversation list */}
          <div
            className={`
              w-full md:w-80 lg:w-96 shrink-0
              ${showMobileSidebar ? 'block' : 'hidden'}
              md:block
              absolute md:relative
              inset-0 md:inset-auto
              z-10 md:z-auto
              bg-background
            `}
          >
            <ConversationList
              conversations={conversations}
              activeConversationId={activeConversationId}
              isLoading={isLoadingConversations}
              onSelectConversation={handleSelectConversation}
              onNewConversation={handleStartComposing}
              onSearch={() => setShowSearchModal(true)}
            />
          </div>

          {/* Main panel - message thread */}
          <div
            className={`
              flex-1 min-w-0
              ${showMobileSidebar ? 'hidden md:block' : 'block'}
            `}
          >
            <MessageThread
              conversation={activeConversation}
              messages={messages}
              pendingMessages={pendingMessages}
              typingUserIds={typingUserIds}
              currentUserId={user?.id || ''}
              isLoading={isLoadingMessages}
              isLoadingMore={isLoadingMore}
              hasMore={hasMoreMessages}
              isSending={isSendingMessage}
              onSendMessage={
                isComposing ? handleComposingSend : handleSendMessage
              }
              onRetryPendingMessage={async (tempId) => {
                await retryPendingMessage(tempId);
              }}
              onRemovePendingMessage={removePendingMessage}
              onEditMessage={handleEditMessage}
              onDeleteMessage={handleDeleteMessage}
              onLoadMore={loadMoreMessages}
              onTypingStart={handleTypingStart}
              onTypingStop={handleTypingStop}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              onMute={handleMute}
              onUnmute={handleUnmute}
              // Composing mode props (iMessage-style flow)
              isComposing={isComposing}
              composingRecipients={composingRecipients}
              onAddComposingRecipient={addComposingRecipient}
              onRemoveComposingRecipient={removeComposingRecipient}
              onCancelComposing={handleCancelComposing}
              onOpenExistingConversation={handleOpenExistingConversation}
              onConvertToGroup={handleConvertToGroup}
            />
          </div>
        </div>
      </div>

      {/* New Conversation Modal */}
      <NewConversationModal
        open={showNewMessageModal}
        onOpenChange={setShowNewMessageModal}
      />

      {/* Message Search Modal */}
      <MessageSearch
        open={showSearchModal}
        onOpenChange={setShowSearchModal}
        conversations={conversations}
        onNavigateToMessage={handleNavigateToMessage}
      />

      {/* Convert to Group Modal */}
      {activeConversation && !activeConversation.isGroup && (
        <ConvertToGroupModal
          open={showConvertToGroupModal}
          onOpenChange={setShowConvertToGroupModal}
          conversation={activeConversation}
        />
      )}
    </>
  );
};

export default MessagesPage;
